<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showDelivery") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper" v-if="!loading">
      <div class="row">
        <!-- Start:: Personal Data Section -->
        <h5 class="mt-5 font-weight-bold col-12">
          {{ $t("TITLES.personal_data") }}
        </h5>

        <!-- Start:: Avatar Image -->
        <div class="col-12 text-center my-4" v-if="data.avatar">
          <div class="avatar-preview">
            <img
              :src="data.avatar.url"
              :alt="data.name"
              style="max-width: 200px; max-height: 200px; border-radius: 10px; cursor: pointer;"
              @click="showImageModal(data.avatar.url)"
            />
            <p class="mt-2 text-muted">{{ $t("PLACEHOLDERS.user_image") }}</p>
          </div>
        </div>
        <!-- End:: Avatar Image -->

        <!-- Start:: Full Name Display -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.delivery_name')"
          v-model="data.name"
          disabled
        />
        <!-- End:: Full Name Display -->

        <!-- Start:: Email Display -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.delivery_email')"
          v-model="data.email"
          disabled
        />
        <!-- End:: Email Display -->

        <!-- Start:: Phone Display -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.delivery_mobile')"
          v-model="data.mobile"
          disabled
        />
        <!-- End:: Phone Display -->

        <!-- Start:: ID Number Display -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.id_card_number')"
          v-model="data.id_card_number"
          disabled
        />
        <!-- End:: ID Number Display -->

        <!-- Start:: Account Status Display -->
        <div class="col-12 my-3">
          <v-chip
            :color="data.is_active ? 'success' : 'error'"
            text-color="white"
            large
          >
            <v-icon left>
              {{ data.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
            {{ data.is_active ? $t('PLACEHOLDERS.active') : $t('PLACEHOLDERS.notActive') }}
          </v-chip>
        </div>
        <!-- End:: Account Status Display -->

        <!-- End:: Account Status Display -->

        <!-- Start:: Vehicle Details Section -->
        <hr class="mt-5 col-12" style="width: 98%" />
        <h5 class="my-5 font-weight-bold col-12">
          {{ $t("PLACEHOLDERS.vehicle_data") }}
        </h5>

        <!-- Start:: Vehicle Plate Number -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.vehicle_plate_number')"
          v-model="data.vehicle_plate_number"
          disabled
        />
        <!-- End:: Vehicle Plate Number -->

        <!-- Start:: Driver License Image -->
        <div class="col-6 text-center my-4" v-if="data.driver_license">
          <div class="license-preview">
            <img
              :src="data.driver_license.url"
              :alt="$t('PLACEHOLDERS.driver_license')"
              style="max-width: 200px; max-height: 200px; border-radius: 10px; cursor: pointer;"
              @click="showImageModal(data.driver_license.url)"
            />
            <p class="mt-2 text-muted">{{ $t("PLACEHOLDERS.driver_license") }}</p>
          </div>
        </div>
        <!-- End:: Driver License Image -->

        <!-- Start:: Statistics Section -->
        <hr class="mt-5 col-12" style="width: 98%" />
        <h5 class="my-5 font-weight-bold col-12">
          {{ $t("TITLES.statistics") }}
        </h5>

        <div class="col-12">
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="pa-4 mb-3">
                <div class="d-flex align-center">
                  <v-icon color="primary" large class="mr-3">mdi-login</v-icon>
                  <div>
                    <p class="text-muted mb-1">{{ $t("STATISTICS.login_count") }}</p>
                    <h3>{{ data.login_count || 0 }}</h3>
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="pa-4 mb-3">
                <div class="d-flex align-center">
                  <v-icon color="info" large class="mr-3">mdi-clock-outline</v-icon>
                  <div>
                    <p class="text-muted mb-1">{{ $t("STATISTICS.last_login_at") }}</p>
                    <h4>{{ data.last_login_at || $t("TABLES.noData") }}</h4>
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="pa-4 mb-3">
                <div class="d-flex align-center">
                  <v-icon color="success" large class="mr-3">mdi-package-variant-closed</v-icon>
                  <div>
                    <p class="text-muted mb-1">{{ $t("STATISTICS.completed_parcels") }}</p>
                    <h3>{{ data.completed_parcels || 0 }}</h3>
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="pa-4 mb-3">
                <div class="d-flex align-center">
                  <v-icon color="error" large class="mr-3">mdi-package-variant-closed-remove</v-icon>
                  <div>
                    <p class="text-muted mb-1">{{ $t("STATISTICS.rejected_packages") }}</p>
                    <h3>{{ data.rejected_packages || 0 }}</h3>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <!-- End:: Statistics Section -->

        <!-- Start:: Bank Account Data Section -->
        <hr class="mt-5 col-12" style="width: 98%" />
        <h5 class="my-5 font-weight-bold col-12">
          {{ $t("TITLES.bank_data") }}
        </h5>

        <!-- Start:: Bank Name -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.bank_name')"
          v-model="data.bank_name"
          disabled
        />
        <!-- End:: Bank Name -->

        <!-- Start:: IBAN Number -->
        <base-input
          col="6"
          type="text"
          :placeholder="$t('PLACEHOLDERS.iban')"
          v-model="data.iban"
          disabled
        />
        <!-- End:: IBAN Number -->
      </div>
    </div>

    <!-- Start:: Loading State -->
    <div v-else class="text-center py-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <!-- End:: Loading State -->

    <!-- Start:: Image Modal -->
    <v-dialog v-model="dialogImage" max-width="800">
      <v-card>
        <v-card-title class="headline">
          {{ $t("BUTTONS.show") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogImage = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="text-center">
          <img :src="selectedImage" style="max-width: 100%; height: auto;" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- End:: Image Modal -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ShowDeliveryMan",

  data() {
    return {
      loading: false,
      dialogImage: false,
      selectedImage: null,
      data: {
        name: null,
        email: null,
        mobile: null,
        is_active: false,
        avatar: null,
        id_card_number: null,
        vehicle_plate_number: null,
        driver_license: null,
        login_count: 0,
        last_login_at: null,
        completed_parcels: 0,
        rejected_packages: 0,
        bank_name: null,
        iban: null,
        wallet_balance: 0,
      },
    };
  },

  computed: {
    ...mapGetters({
      allRoles: "ApiGetsModule/allRoles",
    }),
  },

  methods: {
    // Show image in modal
    showImageModal(imageUrl) {
      this.selectedImage = imageUrl;
      this.dialogImage = true;
    },

    // Fetch delivery man data
    async getDeliveryManData() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          `drivers/${this.$route.params.id}`
        );
        const driver = res.data.data;

        // Populate data
        this.data.name = driver.name;
        this.data.email = driver.email;
        this.data.mobile = driver.mobile;
        this.data.is_active = driver.is_active;
        this.data.id_card_number = driver.id_card_number;
        this.data.vehicle_plate_number = driver.vehicle_plate_number;
        this.data.avatar = driver.avatar;
        this.data.driver_license = driver.driver_license;
        this.data.login_count = driver.login_count;
        this.data.last_login_at = driver.last_login_at;
        this.data.wallet_balance = driver.wallet_balance || 0;
        
        // Statistics from API
        this.data.completed_parcels = driver.packages_count || 0;
        this.data.rejected_packages = driver.bundle_count || 0;
        
        // Bank data from API
        this.data.bank_name = driver.bank || '';
        this.data.iban = driver.iban || '';

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$message.error(error.response?.data?.message || this.$t("MESSAGES.error"));
        this.$router.push("/DeliveryMen/all");
      }
    },
  },

  created() {
    // Fetch delivery man data on component creation
    this.getDeliveryManData();
  },
};
</script>

<style scoped>
.avatar-preview,
.license-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>
